import { notifications } from '@mantine/notifications';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { RowSelectionState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { canAttestOsLotReview } from '../../components/os-lot-reviews/os-lot-review-checks';
import { OsLotReviewsHeader } from '../../components/os-lot-reviews/os-lot-reviews-header';
import OsLotReviewsTable from '../../components/os-lot-reviews/os-lot-reviews-table';
import { useOsLotReviewsFilterOptions } from '../../components/os-lot-reviews/use-os-lot-reviews-filters';
import { osLotReviewsApi } from '../../lib/apis/os-lot-reviews';
import {
  OsLotReviewClause,
  osLotReviewScopeFromSearch,
} from '../../lib/os-lot-review-scope';
import { useOsLotReviewsQueries } from '../../lib/queries/os-lot-reviews';

import { ErrorPage } from './page.error';

import { usePermissions } from '@auth/data-access';
import { MultisectActionDto, useFiltersStore } from '@data-access';
import { FullMainHeight } from '@ui';

export function OsLotReviewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/os/lot-reviews' });
  const scope = osLotReviewScopeFromSearch(search);
  const { isIndustrial } = usePermissions();
  const queries = useOsLotReviewsQueries();
  const filters = useFiltersStore.use['osLotReviews']().filters;
  const sorting = useFiltersStore.use['osLotReviews']().sorting;
  const page = useFiltersStore.use['osLotReviews']().pagination.pageIndex + 1;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const scopedFilters = useMemo(() => {
    const base = filters.filter(
      (filter) =>
        filter.id !== 'clause' &&
        filter.id !== 'campaign_year' &&
        (scope.clause === 'gp' || filter.id !== 'year'),
    );
    const next = [...base, { id: 'clause', value: scope.clause }];
    if (scope.clause === 'new') {
      next.push({ id: 'campaign_year', value: scope.campaign_year });
    }
    return next;
  }, [filters, scope.clause, scope.campaign_year]);

  const listQuery = useQuery({
    ...queries.list({ page, sorting, filters: scopedFilters }),
    placeholderData: keepPreviousData,
  });

  const statsQuery = useQuery({
    ...queries.summary(scopedFilters),
    placeholderData: keepPreviousData,
  });

  const { years, species } = useOsLotReviewsFilterOptions(
    listQuery.data?.results ?? [],
  );

  const selectedIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((id) => id !== 'all')
        .map((id) => Number(id)),
    [rowSelection],
  );

  const selectedCount = rowSelection.all
    ? (listQuery.data?.count ?? selectedIds.length)
    : selectedIds.length;

  const refetch = () => {
    void listQuery.refetch();
    void statsQuery.refetch();
  };

  const setScope = (clause: OsLotReviewClause, campaignYear?: number) => {
    setRowSelection({});
    void navigate({
      to: '/os/lot-reviews',
      search:
        clause === 'new'
          ? {
              clause: 'new',
              campaign_year: campaignYear ?? scope.campaign_year ?? 2026,
            }
          : { clause: 'gp' },
    });
  };

  const exportMutation = useMutation({
    mutationFn: () =>
      osLotReviewsApi.exportExcel({
        page: 1,
        sorting,
        filters: scopedFilters,
      }),
    onError: () => {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
    },
  });

  const attestBulk = useMutation({
    mutationFn: (payload: MultisectActionDto) =>
      osLotReviewsApi.attestBulk(payload, scope),
    onSuccess: () => {
      refetch();
      setRowSelection({});
    },
    onError: () => {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
    },
  });

  const handleAttestSelected = () => {
    if (rowSelection.all) {
      attestBulk.mutate({
        ids: [],
        all: true,
        selectedFilters: scopedFilters,
      });
      return;
    }

    const ids = selectedIds.filter((id) => {
      const lot = listQuery.data?.results.find((row) => row.id === id);
      return lot && canAttestOsLotReview(lot);
    });

    if (ids.length > 0) {
      attestBulk.mutate({ ids });
    }
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <OsLotReviewsHeader
          allOk={statsQuery.data?.all_ok}
          campaignYear={scope.campaign_year ?? 2026}
          clause={scope.clause}
          hasIssue={statsQuery.data?.has_issue}
          isAttestSelectedLoading={attestBulk.isPending}
          isExportLoading={exportMutation.isPending}
          onAttestSelected={handleAttestSelected}
          onCampaignYearChange={(year) => setScope('new', year)}
          onClauseChange={setScope}
          onExport={
            isIndustrial ? undefined : () => exportMutation.mutate()
          }
          selectedCount={selectedCount}
          species={species}
          statsLoading={statsQuery.isLoading}
          total={statsQuery.data?.total}
          years={years}
        />
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <OsLotReviewsTable
          data={listQuery.data?.results ?? []}
          isFetching={listQuery.isFetching}
          isLoading={listQuery.isLoading}
          isPlaceholderData={listQuery.isPlaceholderData}
          rowCount={listQuery.data?.count ?? 0}
          rowSelection={rowSelection}
          scope={scope}
          setRowSelection={setRowSelection}
        />
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

OsLotReviewsPage.Error = ErrorPage;

export default OsLotReviewsPage;
